import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormDetailsReportFields } from 'src/app/reports/form-details-report/form-details-report.fields';
import { FilterOperatorType } from 'src/app/shared/components/odata-filter/models/filter-operator-type';
import { OPERATORS } from 'src/app/shared/components/odata-filter/models/operators';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { FORM_SYSTEM_FILTER_MODELS } from 'src/app/shared/components/odata-filter/models/system-filter-models';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import { ChartData } from 'src/app/shared/types/ng-charts.type';
import { MONTH_SHORT_NAMES, RecentFormResponsesModel } from './recent-form-responses.model';

@Injectable({
  providedIn: 'root',
})
export class RecentFormResponsesService extends BaseODataService {
  public readonly chartData = new Subject<ChartData>();
  private _selectedFormId: string;

  constructor() {
    super();
    this._baseUrl = 'applicationReport';
  }

  getRecentFormResponses(formId: string) {
    this._selectedFormId = formId;
    let queryParams = {
      select: [FormDetailsReportFields.submissionDate],
      filter: this.buildFilter(this.generateFilters()),
    };

    this.getODataResult(this.allResultsFilterUrl, queryParams, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value.length > 0) {
            let result = response.value.map((v) => new RecentFormResponsesModel(v));
            return this.processData(result);
          } else {
            return null;
          }
        })
      )
      .subscribe((result) => this.chartData.next(result));
  }

  private generateFilters() {
    let currentDate = new Date();
    let threeMonthBefore = moment(currentDate).subtract(3, 'M');
    let filters = [
      new SelectedFilter({
        filter: FORM_SYSTEM_FILTER_MODELS.FormSubmissionDateFilterModel,
        filterOperator: OPERATORS.Date.operators.find(
          (op) => op.displayOperator == FilterOperatorType.gt
        ),
        searchValue: moment(threeMonthBefore).startOf('month').format('YYYY-MM-DD'),
      }),
    ];

    if (this._selectedFormId) {
      filters.push(
        new SelectedFilter({
          filter: FORM_SYSTEM_FILTER_MODELS.FormIdFilterModel,
          filterOperator: OPERATORS.Guid.operators.find(
            (op) => op.displayOperator == FilterOperatorType.eq
          ),
          searchValue: this._selectedFormId,
        })
      );
    }
    return filters;
  }

  private processData(data: RecentFormResponsesModel[]): ChartData {
    {
      const keyValueMap = this.generateChartkeyValueMap();

      data.forEach((item: RecentFormResponsesModel) => {
        const key = item.category;
        if (keyValueMap.has(key)) {
          keyValueMap.set(key, keyValueMap.get(key) + 1);
        }
      });

      return {
        chartType: 'bar-vertical',
        datapoint: '',
        dataset: {
          categories: [...keyValueMap.keys()],
          values: [...keyValueMap.values()],
        },
      };
    }
  }

  private generateChartkeyValueMap(): Map<string, number> {
    const keyValueMap = new Map<string, number>();
    let currentDate = new Date();

    for (let i = 3; i >= 0; i--) {
      let date = moment(currentDate).subtract(i, 'M');
      let key = `${MONTH_SHORT_NAMES[moment(date).month()]} ${moment(date).year()}`;
      keyValueMap.set(key, 0);
    }

    return keyValueMap;
  }
}
